import { confirm } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import {
  useCategoriesQuery,
  useDeleteCategory,
  useUpdateCategoryAny,
} from "../api/categories.queries";
import { CategoriesTour, useCategoriesTour } from "../tour/CategoriesTour";
import { CategoriesFilter } from "../ui/CategoriesFilter";
import { CategoriesGrid } from "../ui/CategoriesGrid";
import { CategoriesList } from "../ui/CategoriesList";
import { CategoriesToolbar } from "../ui/CategoriesToolbar";

export function CategoriesPage() {
  const nav = useNavigate({ from: "/app/forms/categories" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/forms/categories",
  });
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const list = useCategoriesQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission([Permissions.forms.create]);
  const canDelete = useActionPermission([Permissions.forms.delete]);
  const updateCategory = useUpdateCategoryAny();
  const deleteCategory = useDeleteCategory();
  const tour = useCategoriesTour();

  const handleToggleActive = (id: string, isActive: boolean) => {
    updateCategory.mutate({ id, isActive });
  };

  const handleDeleteCategory = async (category: {
    id: string;
    name: string;
    activeFormsCount?: number | null;
  }) => {
    const count = category.activeFormsCount ?? 0;
    const ok = await confirm({
      title: "ລຶບຫມວດຫມູ່",
      description:
        count > 0
          ? `ມີຟອມ ${count} ອັນອ້າງອີງຫມວດ «${category.name}» — ຟອມຈະບໍ່ຖືກລຶບ ແຕ່ຈະຖືກຖອກຫມວດ. ຕ້ອງການລຶບຫມວດຫມູ່ນີ້ບໍ່?`
          : `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຫມວດ «${category.name}»?`,
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    await deleteCategory.mutateAsync(category.id);
  };

  return (
    <>
      <Header />

      <Main>
        <CategoriesTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />

        <CategoriesToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/forms/categories/new" })}
          onReorder={() => {
            // TODO: Implement reorder
            // console.log("Reorder categories");
          }}
          onStartTour={tour.startTour}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <div data-tourid="categories-filter">
            <CategoriesFilter
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          </div>

          {viewMode === "grid" ? (
            <div className="p-4" data-tourid="categories-content">
              <CategoriesGrid
                data={list.data?.data ?? []}
                isLoading={list.isLoading}
                canDelete={!!canDelete}
                onEdit={(category) =>
                  nav({
                    to: "/app/forms/categories/$id/edit",
                    params: { id: category.id },
                  })
                }
                onToggleActive={handleToggleActive}
                onDelete={handleDeleteCategory}
                onCreate={() => nav({ to: "/app/forms/categories/new" })}
              />
            </div>
          ) : (
            <div data-tourid="categories-content">
              <CategoriesList
                data={list.data?.data ?? []}
                isLoading={list.isLoading}
                canDelete={!!canDelete}
                offset={search.offset ?? 0}
                limit={search.limit ?? 20}
                totalCount={list.data?.meta?.total ?? 0}
                onPaginationChange={(offset, limit) =>
                  nav({
                    search: {
                      offset,
                      limit,
                      sort: search.sort as
                        | Array<{ field: string; dir: "asc" | "desc" }>
                        | undefined,
                      filters: search.filters as
                        | Array<{ field: string; op: string; value: unknown }>
                        | undefined,
                    },
                  })
                }
                sortBy={search.sort ? search.sort[0]?.field : undefined}
                sortOrder={
                  search.sort
                    ? (search.sort[0]?.dir as "asc" | "desc")
                    : undefined
                }
                onSortingChange={(id, desc) =>
                  nav({
                    search: {
                      offset: search.offset ?? 0,
                      limit: search.limit ?? 20,
                      sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                      filters: search.filters as
                        | Array<{ field: string; op: string; value: unknown }>
                        | undefined,
                    },
                  })
                }
                onEdit={(category) =>
                  nav({
                    to: "/app/forms/categories/$id/edit",
                    params: { id: category.id },
                  })
                }
                onToggleActive={handleToggleActive}
                onDelete={handleDeleteCategory}
              />
            </div>
          )}
        </div>
      </Main>
    </>
  );
}
